// ==========================================================================
// components/AppShell.tsx — the frame every signed-in screen sits in
// FF-025 (9 Oct 2026), redesigned in the FF-026 pass the same day:
//   desktop: deep plum sidebar with the emblem, menu and who's signed in;
//            content on the blush page, up to 1280px.
//   phone:   slim top bar + bottom bar (PhoneNav: Home, Money, camera, Calendar, More).
//   FF-058 (10 Oct): the crest is bigger and takes the theme colour; the
//   account menu (profile, colours, light/dark, Update app, sign out) sits top
//   right on both; the sidebar's bottom card opens My profile.
// Add a screen = one entry in NAV + one <Route> in App.tsx.
// ==========================================================================
import { NavLink, Outlet } from "react-router-dom";
import { Home, Users, Ticket, Car, Laptop, CalendarDays, Wallet, Building2, LineChart, ShoppingBasket, HeartHandshake, Baby, CalendarClock, ChevronRight, type LucideIcon } from "lucide-react";
import { useEffect } from "react";
import { readOnlyPreview } from "@/lib/previewSafety";
import { applyTheme, useMe, useMembership } from "@/lib/household";
import Emblem from "./Emblem";
import AccountMenu, { Avatar } from "./AccountMenu";
import PhoneNav from "./PhoneNav";
import FamilyMenu from "./FamilyMenu";

export const NAV: { to: string; label: string; icon: LucideIcon; end?: boolean; phone?: false }[] = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/money", label: "Money", icon: Wallet },
  { to: "/bills", label: "Bills", icon: CalendarClock },
  { to: "/investments", label: "Investments", icon: LineChart, phone: false },
  { to: "/property", label: "Property", icon: Building2, phone: false },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/kitchen", label: "Kitchen", icon: ShoppingBasket },
  { to: "/us", label: "Us", icon: HeartHandshake },
  { to: "/journal", label: "Journal", icon: Baby },
  { to: "/vehicles", label: "Vehicles", icon: Car },
  { to: "/wfh", label: "WFH", icon: Laptop },
  { to: "/family", label: "Family", icon: Users, phone: false },
  { to: "/tickets", label: "Tickets", icon: Ticket, phone: false },
];

export default function AppShell() {
  const me = useMe(); const membership = useMembership();
  useEffect(() => { if (membership.data?.theme) applyTheme(membership.data.theme); }, [membership.data?.theme]);

  return (
    <div className="min-h-screen lg:flex">
      <aside className="hidden lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-64 lg:shrink-0 lg:flex-col lg:overflow-y-auto lg:bg-panel lg:px-5 lg:py-7 text-panel-ink">
        <FamilyMenu items={NAV}>
          <span className="flex h-24 w-24 items-center justify-center rounded-full shadow-soft" style={{ background: "radial-gradient(circle at 35% 30%, rgb(255 255 255), rgb(var(--cream)) 70%)" }}>
            <Emblem className="h-[76px] w-[76px]" />
          </span>
          <span className="mt-3 font-heading text-[22px] font-semibold tracking-[0.16em] text-panel-ink">O’SULLIVAN</span>
          <span className="mt-0.5 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.32em] text-panel-muted"><span className="h-px w-5 bg-panel-muted/50" />Family home base<span className="h-px w-5 bg-panel-muted/50" /></span>
        </FamilyMenu>
        <nav className="mt-8 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `group flex items-center gap-3 rounded-md px-3 py-2.5 text-[15px] transition ${isActive ? "bg-primary text-primary-ink font-semibold shadow-soft" : "text-panel-muted hover:bg-panel-raised hover:text-panel-ink"}`}>
              <n.icon className="h-[18px] w-[18px]" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <NavLink to="/profile" className="mt-auto flex items-center gap-3 rounded-md bg-panel-raised px-3 py-3 transition hover:brightness-110">
          <Avatar />
          <div className="min-w-0 flex-1 leading-tight">
            <div className="truncate text-sm font-medium">{me?.display_name ?? "Signed in"}</div>
            <div className="text-xs text-panel-muted">My profile</div>
          </div>
          <ChevronRight className="h-4 w-4 text-panel-muted" />
        </NavLink>
      </aside>

      <header className="sticky top-0 z-20 flex items-center justify-between bg-panel px-4 py-2.5 text-panel-ink lg:hidden">
        <FamilyMenu items={NAV} compact>
          <span className="flex h-10 w-10 items-center justify-center rounded-full" style={{ background: "rgb(var(--cream))" }}><Emblem className="h-8 w-8" /></span>
          <span className="leading-none">
            <span className="block font-heading text-[17px] font-semibold tracking-[0.14em]">O’SULLIVAN</span>
            <span className="mt-1 block text-[9px] font-semibold uppercase tracking-[0.3em] text-panel-muted">Family home base</span>
          </span>
        </FamilyMenu>
        <AccountMenu onPanel />
      </header>

      <main className="min-w-0 flex-1 px-4 pb-28 pt-6 lg:px-12 lg:pb-12 lg:pt-5">
        <div className="mx-auto w-full max-w-7xl">
          <div className="mb-3 hidden justify-end lg:flex"><AccountMenu /></div>
          {readOnlyPreview && <p role="status" className="mb-4 rounded-md border border-line bg-surface px-4 py-2 text-sm text-ink-muted">Read-only local preview · saving, uploads and AI actions are disabled.</p>}
          <Outlet />
        </div>
      </main>

      <PhoneNav />
    </div>
  );
}
